import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { requestMagicLink, verifyMagicToken } from '../services/api'

const ERROR_MESSAGES = {
  oauth_denied:   'Sign-in was cancelled.',
  not_authorised: 'Your Google account is not authorised to access this portal.',
  invalid_state:  'Sign-in session expired. Please try again.',
  server_error:   'A server error occurred. Please try again.',
}

export default function Login() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [error, setError]   = useState(null)
  const [mode, setMode]     = useState('google') // 'google' | 'email'
  const [email, setEmail]   = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent]     = useState(false)

  // If already authenticated, skip to dashboard
  useEffect(() => {
    if (sessionStorage.getItem('adminToken') && !searchParams.get('token') && !searchParams.get('magic')) {
      navigate('/', { replace: true })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Handle redirect back from Google OAuth callback
  useEffect(() => {
    const token = searchParams.get('token')
    const err   = searchParams.get('error')

    if (token) {
      sessionStorage.setItem('adminToken', token)
      navigate('/', { replace: true })
      return
    }

    if (err) {
      setError(ERROR_MESSAGES[err] ?? 'Sign-in failed. Please try again.')
    }
  }, [searchParams, navigate])

  // Handle magic link token from email
  useEffect(() => {
    const magic = searchParams.get('magic')
    if (!magic) return

    verifyMagicToken(magic)
      .then(res => {
        sessionStorage.setItem('adminToken', res.data.token)
        navigate('/', { replace: true })
      })
      .catch(err => {
        setError(err.response?.data?.error || 'This sign-in link is invalid or has expired. Please request a new one.')
      })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  function handleGoogleSignIn() {
    const authBase = import.meta.env.VITE_AUTH_BASE_URL || import.meta.env.VITE_API_BASE_URL || ''
    const redirect = `${window.location.origin}/login`
    window.location.href = `${authBase}/api/admin/auth/google?redirect=${encodeURIComponent(redirect)}`
  }

  async function handleEmailSignIn(e) {
    e.preventDefault()
    if (!email.trim()) return
    setSending(true)
    setError(null)
    try {
      await requestMagicLink(email.trim())
      setSent(true)
    } catch {
      setError('Failed to send sign-in link. Please try again.')
    } finally {
      setSending(false)
    }
  }

  // Verifying magic token state
  if (searchParams.get('magic')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="card max-w-sm w-full text-center">
          <LogoHeader />
          {error
            ? <ErrorBox message={error} />
            : <p className="text-gray-500 text-sm">Signing you in…</p>
          }
          {error && (
            <button onClick={() => { setError(null); setMode('email') }} className="mt-4 text-indigo-600 text-sm underline">
              Request a new link
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="card max-w-sm w-full">
        <LogoHeader />

        {error && <ErrorBox message={error} />}

        {mode === 'google' && (
          <>
            <button
              onClick={handleGoogleSignIn}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 active:bg-gray-100 transition-colors font-medium text-gray-700 shadow-sm"
            >
              <GoogleIcon />
              Sign in with Google
            </button>

            <div className="mt-3 text-center">
              <button onClick={() => { setMode('email'); setError(null) }} className="text-indigo-600 text-sm hover:underline">
                Sign in with email instead
              </button>
            </div>

            <p className="mt-4 text-center text-xs text-gray-400">
              Only authorised Osteo3d accounts can access this portal.
            </p>
          </>
        )}

        {mode === 'email' && !sent && (
          <>
            <form onSubmit={handleEmailSignIn} className="space-y-3">
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                autoFocus
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <button
                type="submit"
                disabled={sending}
                className="w-full px-4 py-3 bg-indigo-600 text-white rounded-lg font-medium text-sm hover:bg-indigo-700 disabled:opacity-60 transition-colors"
              >
                {sending ? 'Sending…' : 'Send sign-in link'}
              </button>
            </form>

            <div className="mt-3 text-center">
              <button onClick={() => { setMode('google'); setError(null) }} className="text-gray-500 text-sm hover:underline">
                ← Back to Google sign in
              </button>
            </div>
          </>
        )}

        {mode === 'email' && sent && (
          <div className="text-center space-y-3">
            <div className="text-4xl">📧</div>
            <p className="font-medium text-gray-800">Check your inbox</p>
            <p className="text-sm text-gray-500">
              We sent a sign-in link to <strong>{email}</strong>. It expires in 15 minutes.
            </p>
            <button
              onClick={() => { setSent(false); setEmail('') }}
              className="text-indigo-600 text-sm hover:underline"
            >
              Use a different email
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function LogoHeader() {
  return (
    <div className="text-center mb-8">
      <div className="flex justify-center mb-5">
        <img
          src="/osteo3d-logo.png"
          alt="Osteo3d — 3d printing healthcare"
          height={52}
          style={{ height: 52, width: 'auto', display: 'block' }}
        />
      </div>
      <h2 className="text-base font-semibold text-gray-700">Osteo3d Books</h2>
      <p className="text-gray-400 text-sm mt-0.5">Invoicing &amp; Expense Management</p>
    </div>
  )
}

function ErrorBox({ message }) {
  return (
    <div className="mb-5 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
      <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
      </svg>
      {message}
    </div>
  )
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908C16.658 14.013 17.64 11.705 17.64 9.2z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
      <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z"/>
    </svg>
  )
}
